import type { PrV2CheckRunResponse } from './types';

import { isCubicCheckRunName } from '@/lib/github/cubic-check-run-names';

export type PrV2CheckBucket = 'failed' | 'running' | 'passed';

const CHECK_PASSING_CONCLUSIONS = new Set(['success', 'neutral', 'skipped']);
const CHECK_FAILING_CONCLUSIONS = new Set([
  'failure',
  'cancelled',
  'timed_out',
  'action_required',
  'startup_failure',
  'stale',
]);

function toTimestamp(value: string | null | undefined): number {
  if (!value) {
    return -Infinity;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? -Infinity : timestamp;
}

function latestActivityTimestamp(check: PrV2CheckRunResponse): number {
  return Math.max(toTimestamp(check.completedAt), toTimestamp(check.startedAt));
}

function numericId(value: string): number {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? -Infinity : parsed;
}

function comparePrV2CheckRunsByLatestActivity(
  a: PrV2CheckRunResponse,
  b: PrV2CheckRunResponse,
): number {
  const latestActivityDiff = latestActivityTimestamp(b) - latestActivityTimestamp(a);

  if (latestActivityDiff !== 0) {
    return latestActivityDiff;
  }

  const completedAtDiff = toTimestamp(b.completedAt) - toTimestamp(a.completedAt);

  if (completedAtDiff !== 0) {
    return completedAtDiff;
  }

  const startedAtDiff = toTimestamp(b.startedAt) - toTimestamp(a.startedAt);

  if (startedAtDiff !== 0) {
    return startedAtDiff;
  }

  return numericId(b.githubCheckRunId) - numericId(a.githubCheckRunId);
}

export function classifyPrV2CheckRun(check: PrV2CheckRunResponse): PrV2CheckBucket {
  if (check.status !== 'completed' || !check.conclusion) {
    return 'running';
  }

  if (CHECK_FAILING_CONCLUSIONS.has(check.conclusion)) {
    return 'failed';
  }

  if (CHECK_PASSING_CONCLUSIONS.has(check.conclusion)) {
    return 'passed';
  }

  return 'running';
}

export function isPrV2CheckPassing(check: PrV2CheckRunResponse): boolean {
  return classifyPrV2CheckRun(check) === 'passed';
}

const CHECK_BUCKET_SORT_ORDER: Record<PrV2CheckBucket, number> = {
  failed: 0,
  running: 1,
  passed: 2,
};

export function comparePrV2CheckRunsForDisplay(
  a: PrV2CheckRunResponse,
  b: PrV2CheckRunResponse,
): number {
  const bucketOrderDiff =
    CHECK_BUCKET_SORT_ORDER[classifyPrV2CheckRun(a)] -
    CHECK_BUCKET_SORT_ORDER[classifyPrV2CheckRun(b)];

  if (bucketOrderDiff !== 0) {
    return bucketOrderDiff;
  }

  return comparePrV2CheckRunsByLatestActivity(a, b);
}

type NormalizePrV2CheckRunsForDisplayParams = {
  checks: PrV2CheckRunResponse[];
  currentHeadSha: string | null;
};

export function normalizePrV2CheckRunsForDisplay({
  checks,
  currentHeadSha,
}: NormalizePrV2CheckRunsForDisplayParams): PrV2CheckRunResponse[] {
  const latestChecksByName = new Map<string, PrV2CheckRunResponse>();

  for (const check of checks) {
    if (currentHeadSha && check.headSha && check.headSha !== currentHeadSha) {
      continue;
    }

    const existing = latestChecksByName.get(check.name);
    const checkMatchesCurrentHead = Boolean(currentHeadSha && check.headSha === currentHeadSha);
    const existingMatchesCurrentHead = Boolean(
      currentHeadSha && existing?.headSha === currentHeadSha,
    );

    if (
      !existing ||
      (checkMatchesCurrentHead && !existingMatchesCurrentHead) ||
      (checkMatchesCurrentHead === existingMatchesCurrentHead &&
        comparePrV2CheckRunsByLatestActivity(check, existing) < 0)
    ) {
      latestChecksByName.set(check.name, check);
    }
  }

  return Array.from(latestChecksByName.values()).sort(comparePrV2CheckRunsForDisplay);
}

export type PrV2ChecksDisplay = {
  /** Current-head runs with cubic's own runs dropped and reruns collapsed to the latest. */
  sortedChecks: PrV2CheckRunResponse[];
  /** Required checks other than cubic's own, whose runs are dropped. */
  requiredCheckNames: Set<string>;
  /** Required checks that have not reported a run on the current head yet. */
  pendingRequiredChecks: string[];
  /** GitHub treats neutral and skipped conclusions as satisfying required checks. */
  requiredPassedCount: number;
  /** Runs per bucket; skipped runs are counted apart from the other passing ones. */
  counts: {
    failed: number;
    running: number;
    passed: number;
    skipped: number;
  };
};

/**
 * The checks list and required-check progress shown by the PR page sidebar and the /code PR
 * tab, so both count the same runs.
 */
export function buildPrV2ChecksDisplay({
  checks,
  headSha,
  requiredChecks = [],
}: {
  checks: PrV2CheckRunResponse[];
  headSha: string | null;
  requiredChecks?: readonly string[];
}): PrV2ChecksDisplay {
  const sortedChecks = normalizePrV2CheckRunsForDisplay({
    checks: checks.filter((check) => !isCubicCheckRunName(check.name)),
    currentHeadSha: headSha,
  });
  const requiredCheckNames = new Set(
    requiredChecks.filter((checkName) => !isCubicCheckRunName(checkName)),
  );
  const reportedCheckNames = new Set<string>();
  const counts = { failed: 0, running: 0, passed: 0, skipped: 0 };
  let requiredPassedCount = 0;

  // Runs are unique by name after normalizing, so each required check counts at most once.
  for (const check of sortedChecks) {
    reportedCheckNames.add(check.name);
    const bucket = classifyPrV2CheckRun(check);

    if (bucket === 'passed' && requiredCheckNames.has(check.name)) {
      requiredPassedCount += 1;
    }

    if (bucket === 'passed' && check.conclusion === 'skipped') {
      counts.skipped += 1;
    } else {
      counts[bucket] += 1;
    }
  }

  return {
    sortedChecks,
    requiredCheckNames,
    pendingRequiredChecks: Array.from(requiredCheckNames).filter(
      (checkName) => !reportedCheckNames.has(checkName),
    ),
    requiredPassedCount,
    counts,
  };
}

export type PrV2CheckSummary = {
  failed: number;
  passed: number;
  running: number;
  total: number;
};

export function getInboxCheckSummaryKey(locator: {
  owner: string;
  repo: string;
  number: number;
}): string {
  return `${locator.owner.toLowerCase()}/${locator.repo.toLowerCase()}#${locator.number}`;
}

/**
 * Check counts in the inbox's shape, shared by the inbox, the /code PR card and the PR page. Skipped
 * runs pass, and required checks that have not reported yet are still running. The inbox has no
 * required-check names, so until those report it counts fewer runs than the other two.
 */
export function summarizePrV2ChecksDisplay({
  counts,
  pendingRequiredChecks,
}: Pick<PrV2ChecksDisplay, 'counts' | 'pendingRequiredChecks'>): PrV2CheckSummary {
  const running = counts.running + pendingRequiredChecks.length;
  const passed = counts.passed + counts.skipped;

  return { failed: counts.failed, passed, running, total: counts.failed + running + passed };
}

/** An inbox row's check counts, over the same runs the PR page lists (cubic's own runs dropped). */
export function summarizePrV2CheckRuns({
  checks,
  headSha,
}: {
  checks: PrV2CheckRunResponse[];
  headSha: string | null;
}): PrV2CheckSummary {
  return summarizePrV2ChecksDisplay(buildPrV2ChecksDisplay({ checks, headSha }));
}
